import type { FunctionComponent } from 'react';
import { memo } from 'react';

import type { ModifierGroupFormValue, PreviewModifier } from 'helpers/menuPreview';

import { CheckboxGroupItem } from './CheckboxGroupItem';
import { GroupInputItem } from './GroupInputItem';
import { RadioGroupItem } from './RadioGroupItem';

type ModifierGroupFormItemProps = {
  name?: string;
  groupKey: string;
  modifiers: PreviewModifier[];
  minimum?: number;
  maximum?: number;
  onModifierChange: (groupKey: string, value: ModifierGroupFormValue) => void;
  onDrillIn: (modifierKey: string, modifierName: string) => void;
  individualMaximum?: number;
  individualMinimum?: number;
  groupValue?: ModifierGroupFormValue;
  showTrailingDivider?: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  uuid?: string;
};

const ModifierGroupFormItemComponent: FunctionComponent<ModifierGroupFormItemProps> = ({
  name,
  groupKey,
  modifiers,
  minimum,
  maximum,
  onModifierChange,
  onDrillIn,
  individualMaximum,
  individualMinimum,
  groupValue,
  showTrailingDivider = true,
}) => {
  const getFormItem = () => {
    if ((individualMaximum ?? 0) > 0 || (individualMinimum ?? 0) > 0) {
      return GroupInputItem;
    }

    if (minimum === maximum && maximum === 1) {
      return RadioGroupItem;
    }

    return CheckboxGroupItem;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const FormItem = getFormItem() as any;

  if (modifiers.length === 0) {
    return null;
  }

  return (
    <FormItem
      key={groupKey}
      label={name}
      {...{
        name,
        groupKey,
        modifiers,
        onModifierChange,
        onDrillIn,
        individualMaximum,
        individualMinimum,
        groupValue,
        showTrailingDivider,
      }}
    />
  );
};

/**
 * Remounting the form (<MenuManagementPreviewItemModal />) can cause the form values to be un-set.
 * We memo-ize this component by uuid so that it does not re-render unless the group changes.
 * Related ticket: https://chownow.atlassian.net/browse/CN-39780
 */
export const ModifierGroupFormItem = memo(
  ModifierGroupFormItemComponent,
  (previousProps, nextProps) => previousProps?.uuid === nextProps?.uuid,
);
